import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import IntroPage from './components/IntroPage.jsx';
import LoginPage from './components/LoginPage.jsx';
import OAuthPage from './components/OAuthPage.jsx';
import HomePage from './components/HomePage.jsx';
import ProfilePage from './components/ProfilePage.jsx';
import PerformersSearchPage from './components/PerformersSearchPage.jsx';
import RegisterBand from './components/RegisterBand.jsx';
import Footer from './components/Footer.jsx';
import PerformerHome from './components/PerformerHome.jsx';
import PublicSongList from './components/PublicSongList.jsx';
import SongListPageWrapper from './components/SongListPageWrapper.jsx';
import EditTagsPageWrapper from './components/EditTagsPageWrapper.jsx';
import StripeIncompleteRedirect from './components/StripeIncompleteRedirect.jsx';
import SongQueueRedirect from './components/SongQueueRedirect.jsx';
import RequireAuth from './components/RequireAuth.jsx';
import RequireBandMember, { bandPagePath } from './components/RequireBandMember.jsx';

import './App.css';  // Import styles
import { ThemeProvider, useTheme } from './ThemeContext.jsx';  // Import the ThemeProvider
import './styles/themes.css';

import BandLayoutPage from './components/partial/layout/BandLayoutPage.jsx';

import AddSong from './components/partial/AddSong.jsx';
import PerformerEvent from './components/partial/PerformerEvent.jsx';
import ShowDigestPage from './components/ShowDigestPage.jsx';
import BandFollowersPage from './components/BandFollowersPage.jsx';
import StreamPage from './components/StreamPage.jsx';
import StreamerDashboard from './components/StreamerDashboard.jsx';
import StreamerSongListImportPage from './components/StreamerSongListImportPage.jsx';
import OBSOverlay from './components/OBSOverlay.jsx';
import OBSSetupPage from './components/OBSSetupPage.jsx';

import PerformerNavEvents from './components/partial/navigation/PerformerNavEvents.jsx';
import ListenerHistoryPage from './components/ListenerHistoryPage.jsx';
import ListenerAchievementsPage from './components/ListenerAchievementsPage.jsx';
import NotificationsPage from './components/NotificationsPage.jsx';
import SkinShopPage from './components/SkinShopPage.jsx';
import UpgradePage from './components/UpgradePage.jsx';
import { NotificationsProvider } from './contexts/NotificationsContext.jsx';
import { LocaleProvider, useT } from './contexts/LocaleContext.jsx';
import { LocalePreferenceSync } from './contexts/LocalePreferenceSync.jsx';
import { AccountFxPreferencesProvider } from './contexts/AccountFxPreferences.jsx';
import PhotosensitivityWarning from './components/partial/PhotosensitivityWarning.jsx';
import { BroadcastSurfaceProvider } from './contexts/BroadcastSurface.jsx';
import { AnalyticsSync } from './contexts/AnalyticsSync.jsx';
import { OAUTH_CALLBACK_PATH } from './_helpers/oauthFlowState.js';
import { loadFeatures } from './_helpers/features.js';
// Last, after every other stylesheet: the site-wide reduced-motion layer (ENG-837).
import './styles/reducedMotion.css';

/**
 * The app footer, everywhere but the OBS overlay (`/obs/...`): the overlay is
 * itself the broadcast, and nothing app-level may show on it, not even for the
 * frame before its stylesheet hides the footer (ENG-840).
 */
const AppFooter = () => {
  const { pathname } = useLocation();
  return pathname.startsWith('/obs/') ? null : <Footer />;
};

function App() {
  const t = useT();
  const [showLoginPage, setShowLoginPage] = useState(false);
  const { applyStoredTheme } = useTheme();

  useEffect(() => {
    applyStoredTheme();
  }, [applyStoredTheme]);

  // Ask for the operator feature flags once, up front, so the answer is
  // usually in before a song starts (ENG-783); `useFeatureFlag` shares it.
  useEffect(() => {
    loadFeatures();
  }, []);

  const handleLoginClick = () => {
    setShowLoginPage(true);
  };

  return (
      <Router>
        {/* These re-check the session on every route change, so they sit inside the Router. */}
        {/* PostHog (ENG-857): identifies the session; nothing at all without a key. */}
        <AnalyticsSync />
        <LocalePreferenceSync>
        <AccountFxPreferencesProvider>
        <NotificationsProvider>
        {/* Nothing displays over a broadcast: pages with a player tell app-level overlays here. */}
        <BroadcastSurfaceProvider>
        {/* The first-login photosensitivity warning (ENG-836), once for the whole app. */}
        <PhotosensitivityWarning />
        <div className="App">
          <div className="App-content">
            <Routes>
              <Route path="/" element={showLoginPage ? <LoginPage /> : <IntroPage onLoginClick={handleLoginClick} />} />
              {/* The same constant the replay guards match on, so the route
                  and what counts as the callback URL cannot drift apart. */}
              <Route path={OAUTH_CALLBACK_PATH} element={<OAuthPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register-band" element={<RegisterBand />} />
              <Route path="/stripe_incomplete" element={<StripeIncompleteRedirect />} />
              <Route path="/performers" element={<PerformersSearchPage />} />
              {/* The skins shop (ENG-823): public to browse; buying asks for a session. */}
              <Route path="/shop" element={<SkinShopPage />} />
              {/* Account pages: signed out, they go to /login and come back
                  after signing in (ENG-499). */}
              <Route element={<RequireAuth />}>
                <Route path="/home" element={<HomePage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/history" element={<ListenerHistoryPage />} />
                <Route path="/achievements" element={<ListenerAchievementsPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                {/* Plans and plan changes (ENG-892): the header's UPGRADE pill. */}
                <Route path="/upgrade" element={<UpgradePage />} />
              </Route>
              <Route path="/error" element={<div>{t('app.errors.authTokenNotFound')}</div>} />

              {/* Public routes */}
              <Route path="/public/bands/:bandid/songlist" element={<PublicSongList />} />

              <Route path="/performers/:bandid" element={<PerformerHome />} />
              {/* Band admin pages: members only; everyone else goes to the
                  public song list, or for events the band page (ENG-500). */}
              <Route path="/performers/:bandid/song-list" element={<RequireBandMember><SongListPageWrapper /></RequireBandMember>} />
              {/* Retired Song Queue page (ENG-315) — redirect stale links to the song list */}
              <Route path="/performers/:bandid/song-queue" element={<SongQueueRedirect />} />

              <Route path="/performers/:bandid/events" element={
                <RequireBandMember redirectTo={bandPagePath}>
                  <BandLayoutPage pageType="events">
                    <PerformerEvent />
                  </BandLayoutPage>
                </RequireBandMember>
              } />

              {/* A show's post-show digest (ENG-446). The "show report" inbox
                  notification links here (notifications.ShowReportPath). */}
              <Route path="/performers/:bandid/shows/:showId/report" element={
                <RequireBandMember redirectTo={bandPagePath}>
                  <BandLayoutPage pageType="digest" previousPage="/performers/:bandid/events" showHamburgerMenu={false}>
                    <ShowDigestPage />
                  </BandLayoutPage>
                </RequireBandMember>
              } />

              <Route path="/performers/:bandid/followers" element={
                <BandLayoutPage showHamburgerMenu={false} pageType="followers" previousPage="/performers/:bandid">
                  <BandFollowersPage />
                </BandLayoutPage>
              } />

              <Route path="/performers/:bandid/edit-tags" element={<RequireBandMember><EditTagsPageWrapper /></RequireBandMember>} />

              <Route path="/performers/:bandid/add-song" element={
                <RequireBandMember>
                  <BandLayoutPage
                    previousPage="/performers/:bandid/song-list"
                    pageType="add-song"
                    showHamburgerMenu={false}
                  >
                    <AddSong />
                  </BandLayoutPage>
                </RequireBandMember>} />

              <Route path="/performers/:bandid/import-songs" element={<RequireBandMember><StreamerSongListImportPage /></RequireBandMember>} />
              <Route path="/performers/:bandid/stream" element={<StreamPage />} />
              <Route path="/performers/:bandid/streamer" element={<StreamerDashboard />} />
              <Route path="/performers/:bandid/obs-setup" element={<OBSSetupPage />} />

              {/* OBS browser-source overlay — public, transparent */}
              <Route path="/obs/bands/:bandid" element={<OBSOverlay />} />

              {/* No band in this URL, so there is no song list to show; old links
                  land on performer search (ENG-514). */}
              <Route path="/public-song-list" element={<Navigate to="/performers" replace />} />
            </Routes>

            

          </div>
          <AppFooter />
        </div>
        </BroadcastSurfaceProvider>
        </NotificationsProvider>
        </AccountFxPreferencesProvider>
        </LocalePreferenceSync>
      </Router>
  );
}

// No RadioProvider (ENG-520): the radio is not shipped, and the provider
// loaded the WASM audio module and patched every <audio> element on each
// visit for it. Whatever mounts the radio mounts the provider with it, and
// the module loads then.
// Rule (ENG-784): any radio entry point (route, nav link, player, the radio
// provider) renders inside <FeatureGate feature="radio">
// (components/FeatureGate.jsx), with the provider mounted inside that gate,
// never here at the app root. src/test/radioFeatureGate.test.js enforces it.
const AppWrapper = () => (
  <LocaleProvider>
    <ThemeProvider>
      <App />
    </ThemeProvider>
  </LocaleProvider>
);

export default AppWrapper;
